import React, { useEffect, useState } from 'react';
import { Alert, Button, Tag, message, Modal, Radio, Tooltip } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import ImageUploader from '../../../../components/ImageUploader';
import { useReviewContactDraft } from '../hooks/useReviewContactDraft';
import { useTranslation } from 'react-i18next';
import { useMutation, useLazyQuery } from '@apollo/client';
import {
  GET_COOPERATION_WAREHOUSE_DETAIL,
  FindCooperationWarehouseByIdResponse,
} from '../../cooperation-warehouse/graphql/queries/getCooperationWarehouses';
import {
  RESOLVE_REVIEW_POOL_AS_MERGE_TO_EXISTING,
  ResolveReviewPoolAsMergeToExistingResponse,
  ResolveReviewPoolMergeInput,
  RESOLVE_REVIEW_POOL_AS_NEW_STATION,
  ResolveReviewPoolAsNewStationResponse,
  ResolveReviewPoolNewStationInput,
  RESOLVE_REVIEW_POOL_AS_UPDATE_EXISTING,
  ResolveReviewPoolAsUpdateExistingResponse,
  ResolveReviewPoolExistingStationInput,
  RESOLVE_REVIEW_POOL_AS_CONFIRM_APPOINTMENT,
  ResolveReviewPoolAsConfirmAppointmentResponse,
  RESOLVE_REVIEW_POOL_AS_AB_MAPPING,
  ResolveReviewPoolAsAbMappingResponse,
  ResolveReviewPoolAbMappingInput,
} from '../graphql/mutations/resolveReviewPool';
import type { AbMappingSubmit } from './AbMappingWizard';
import {
  PREVIEW_AB_RETIRE,
  PreviewAbRetireData,
  AbRetireBlockedReason,
} from '../graphql/queries/previewAbRetire';
import {
  ReviewPoolItem,
  ReviewDiagnosisType,
  ReviewSourceTag,
  ReviewOperation,
  ReviewContactResult,
  ReviewAbnormalReportData,
} from '../types/reviewPool';
import { getUserInfo } from '../../../../utils/auth';
import { canReportAddressAbnormal, getOperationPlan } from '../utils/reviewOperations';
import { useAddressAbnormalGray } from '../../../../hooks/useAddressAbnormalGray';
import AbnormalReportCard from './AbnormalReportCard';
import ReportAddressAbnormalModal from './ReportAddressAbnormalModal';
import {
  CREATE_DRAFT_COOPERATION_WAREHOUSE,
  CreateDraftCooperationWarehouseResponse,
} from '../graphql/mutations/draftCooperationWarehouse';
import { mapCwToForm, normalizeCc } from '../utils/cwFormMapper';
import { buildSuggestedSiteName } from '../utils/siteName';
import { formatPhone } from './ReviewAddressCard';
import ModifyAddressModal from './ModifyAddressModal';
import PickupSiteFormDrawer, { SiteAddressForSave } from './PickupSiteFormDrawer';
import AbMappingWizard from './AbMappingWizard';
import MergeToExistingWizard from './MergeToExistingWizard';
import PickupAppointmentDrawer from '../../appointment/components/PickupAppointmentDrawer';
import {
  CONFIRM_PICKUP_APPOINTMENTS,
  ConfirmPickupAppointmentsResponse,
} from '../../appointment/graphql/mutations/confirmPickupAppointments';
import { translateGraphQLError } from '../../../../utils/graphqlUtils';
import { LinkStatus, PickupSiteFormMode, PickupSiteFormValues } from '../types/pickupSite';

interface Props {
  item: ReviewPoolItem;
  /** resolve 成功后回调（清空选中 + 刷新列表）。 */
  onResolved?: () => void;
  /**
   * **就地**更新选中条目（2.2 需求点1 的上报 / 编辑 / 撤销地址异常）。
   *
   * <p>不能复用 onResolved —— 上报不是 resolve，条目仍待处理（process_status 不变），
   * 清空选中会让运营以为卡片被移出审核池。
   *
   * <p><b>刻意不是"刷新列表"</b>：页面的 reloadToken 是给切 hub / resolve 用的，
   * NewAddressReviewList 收到它会 `setItems([])` 然后只拉第 1 页；而"回抛最新条目"的同步
   * effect 又带着 `loadedPage !== 1` 的前置条件。两者叠加的结果是——已经滚到第 2 页往后的运营
   * 一上报就当场丢失自己正在处理的那张卡片（选中被 onSelectedMissing 清掉、右侧详情空白）。
   * 上报只改这一个条目的一个字段，没有任何理由动整张列表。
   */
  onPatchSelected?: (patch: Partial<ReviewPoolItem>) => void;
}

/**
 * 用刚提交的内容拼出条目上的 abnormalReport，供上报/编辑后就地更新。
 *
 * <p>`proofUrls` 直接用服务端随 mutation 返回的预签名地址 —— 前端只有 objectKey，签不出可读地址；
 * 而 ImageUploader 内部那个 blob URL 也不能借用：它的清理 effect 依赖 `[fileList]`，
 * 每次列表变更都会 revoke 上一批，指望不上。
 *
 * <p>刻意**不**按下标与 proofKeys 配对补空串：服务端对个别预签名失败的 key 会直接跳过，
 * 返回值可能比 keys 短；而 OnsiteProofs 的判据是「urls 非空就整组按 urls 渲染」，
 * 混入空串就是裂图。短一张就少显示一张，与列表查询里 abnormalProofDisplayUrls 的行为一致。
 */
const buildLocalAbnormalReport = (saved: {
  reason?: string;
  remark?: string;
  proofKeys: string[];
  proofUrls: string[];
}): ReviewAbnormalReportData => ({
  reason: saved.reason,
  remark: saved.remark,
  proofKeys: saved.proofKeys,
  proofUrls: saved.proofUrls,
  // 展示用，给带 Z 的 UTC 串。渲染侧 formatHubTimeFull → hubDayjs → normalizeUtcString 见到 Z
  // 会原样保留、按 UTC 解析后转 hub 时区；而服务端返回的是**无后缀**的 UTC 串，同样被补 Z 后
  // 走一样的路径。所以本地拼的这份与刷新后拿到的那份，渲染结果一致，不会出现整点级跳变。
  reportedAt: new Date().toISOString(),
  reportedByName: getUserInfo()?.account?.name,
});

const ReviewAddressDetail: React.FC<Props> = ({ item, onResolved, onPatchSelected }) => {
  const { t } = useTranslation();
  const aw = (k: string) => t(`pickup.addressReviewPool.abWizard.${k}`) as string;
  // 入口拦截在等后端返回：操作区整体置灰 + AB 按钮转圈。缺了它运营会重复点，
  // 每次点都发一次查询、可能弹出多个 Modal 叠在一起。
  const [abEntryChecking, setAbEntryChecking] = useState(false);
  // AB 映射入口拦截用（见 guardAbMappingEntry）；network-only：在途单状态随时被司机端改变，缓存会拦错
  const [previewAbRetireForEntry] = useLazyQuery<
    PreviewAbRetireData,
    { reviewPoolId: number; targetBCooperationWarehouseId: number | null; fullySelected: boolean }
  >(PREVIEW_AB_RETIRE, { fetchPolicy: 'network-only' });
  // 联系结果与两组凭证走**服务端草稿**（落在审核池条目上，随列表返回）。
  // 两步流程的真实节奏是「今天首联上传截图 → 隔一段时间再二联」，中间必然切卡片、甚至换人换设备；
  // 曾用 localStorage，但恢复出来的截图画不出缩略图（ImageUploader 需 displayUrls，blob: URL 又刷新即失效），
  // 且换浏览器就丢 —— 详见 useReviewContactDraft 的说明与 plan §2.5.2。
  const { draft, setDraft, clearDraft, restored: draftRestored } = useReviewContactDraft(
    item.id,
    item.contactDraft,
  );
  const { contact, proofKeys, proofUrls, secondContact, secondProofKeys, secondProofUrls } = draft;
  const setContact = (v: ReviewContactResult | undefined) => setDraft((d) => ({ ...d, contact: v }));
  const setProofKeys = (v: string[]) => setDraft((d) => ({ ...d, proofKeys: v }));
  const setSecondContact = (v: ReviewContactResult | undefined) =>
    setDraft((d) => ({ ...d, secondContact: v }));
  const setSecondProofKeys = (v: string[]) => setDraft((d) => ({ ...d, secondProofKeys: v }));

  /**
   * 条目处理成功的统一出口：先清草稿再通知上层刷新。
   * 不清的话条目若因"部分处理"重新出现在列表里，会带出上一轮的陈旧选择与截图。
   */
  const resolvedAndClearDraft = () => {
    clearDraft(item.id);
    onResolved?.();
  };
  const [contactsOpen, setContactsOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewId, setViewId] = useState<number | undefined>(undefined);
  const [viewValues, setViewValues] = useState<Partial<PickupSiteFormValues>>({});
  const [viewAddress, setViewAddress] = useState<SiteAddressForSave | undefined>(undefined);
  const [modifyOpen, setModifyOpen] = useState(false);
  const [siteDrawerOpen, setSiteDrawerOpen] = useState(false);
  const [siteDrawerMode, setSiteDrawerMode] = useState<PickupSiteFormMode>('create');
  // 草稿预占（新建站点）：createDraft 拿真实 cw.id 占位传 drawer。
  // 关闭时不删草稿——运营保存前就拿该 id 建了微信群，重进必须拿回同一 id，否则群名与站点 ID 错位；
  // 同 sourceKey 未转正的草稿由后端复用，超 24h 未转正由清理任务回收。
  const [draftCwId, setDraftCwId] = useState<number | undefined>(undefined);
  // 历史存量关联站点名：由审核池列表接口随 relatedWarehouseId 一并返回（pickup-service 批量取名，无 N+1）
  const relatedStationName = item.relatedWarehouseName;
  const [createDraft] = useMutation<
    CreateDraftCooperationWarehouseResponse,
    { hubId: number; sourceKey: string }
  >(CREATE_DRAFT_COOPERATION_WAREHOUSE);
  // 历史存量·更新站点：从既有 cw 预填的表单值/地址（带出原始信息供修改），create 模式则用 siteInitialValues
  const [editValues, setEditValues] = useState<Partial<PickupSiteFormValues>>({});
  const [editAddress, setEditAddress] = useState<SiteAddressForSave | undefined>(undefined);
  /** 「更新原站点」目标 cw 的 link_status 原值（判是否豁免地址标准化） */
  const [editDbLinkStatus, setEditDbLinkStatus] = useState<number | undefined>(undefined);
  const [abOpen, setAbOpen] = useState(false);
  /** 从「补录实际上门地址」带进 AB 向导的 B 地址；直接点「AB 地址映射」进入时为空 */
  const [abPrefill, setAbPrefill] = useState<SiteAddressForSave | undefined>();
  const [mergeOpen, setMergeOpen] = useState(false);
  // 确认预约单（历史存量·未联系到·当日有单）：复用预约单页建单抽屉 detail 只读态展示真实当日单，底部「确认当日预约单」→ resolve。
  const [confirmApptOpen, setConfirmApptOpen] = useState(false);
  const [confirmApptLoading, setConfirmApptLoading] = useState(false);
  // 创建预约单（历史存量·未联系到·当日无单）：复用预约单页建单抽屉手工填单，区别于 confirm 模式的自动确认。
  const [createApptOpen, setCreateApptOpen] = useState(false);

  const [loadStation] = useLazyQuery<FindCooperationWarehouseByIdResponse, { id: number }>(
    GET_COOPERATION_WAREHOUSE_DETAIL,
    { fetchPolicy: 'no-cache' },
  );

  const [mergeToExisting] = useMutation<
    ResolveReviewPoolAsMergeToExistingResponse,
    { input: ResolveReviewPoolMergeInput }
  >(RESOLVE_REVIEW_POOL_AS_MERGE_TO_EXISTING);

  const [resolveAsNewStation] = useMutation<
    ResolveReviewPoolAsNewStationResponse,
    { input: ResolveReviewPoolNewStationInput }
  >(RESOLVE_REVIEW_POOL_AS_NEW_STATION);
  const [resolveAsUpdateExisting] = useMutation<
    ResolveReviewPoolAsUpdateExistingResponse,
    { input: ResolveReviewPoolExistingStationInput }
  >(RESOLVE_REVIEW_POOL_AS_UPDATE_EXISTING);

  // 站点保存成功（saveCooperationWarehouse 返回 cwId）后：按建站/更新站点分别回填解卡 + mark processed
  const handleSiteSaved = async (cooperationWarehouseId: number) => {
    // 草稿已随保存转正（status 3→1），后端按 status=DRAFT 过滤，同 sourceKey 下次点击会正确新建
    setDraftCwId(undefined);
    try {
      if (siteDrawerMode === 'edit') {
        const resp = await resolveAsUpdateExisting({
          variables: {
            input: {
              reviewPoolId: item.id,
              targetCooperationWarehouseId: cooperationWarehouseId,
              // 2.1 需求点1：仅终态分支传 5（确认无法建联）+ 两组凭证；
              // 其余场景两者均为 undefined，后端行为与 2.0 完全一致（不动 link_status、无凭证）
              linkStatus: isUnreachableTerminal ? LinkStatus.Unreachable : undefined,
              proofS3KeysJson: isUnreachableTerminal ? buildProofS3KeysJson() : undefined,
            },
          },
        });
        if (resp.data?.resolveReviewPoolAsUpdateExisting) {
          resolvedAndClearDraft();
        } else {
          message.error(t('pickup.addressReviewPool.siteForm.resolveFailed'));
        }
      } else {
        const resp = await resolveAsNewStation({
          variables: {
            input: {
              reviewPoolId: item.id,
              cooperationWarehouseId,
              linkStatus: presetLinkStatus ?? undefined,
              // 首次建联(电联1)联络记录截图 → op3 content.proofS3Keys；
              // 2.1 终态分支改为分组对象 {"first":[],"second":[]}，由 pickup 侧拆 op3+op6
              proofS3KeysJson: buildProofS3KeysJson(),
            },
          },
        });
        if (resp.data?.resolveReviewPoolAsNewStation) {
          resolvedAndClearDraft();
        } else {
          // 新建路径专用文案：站点此刻**已由 api-server 建好**（独立事务），而后端这步可能已提交
          // 也可能回滚 —— 两种都不能让运营"重试"：重试会走完整建站流程再建一个站点，
          // 若条目其实已 DONE，resolve 会被幂等守卫跳过，于是留下一个无人认领的悬挂站点。
          message.error(t('pickup.addressReviewPool.siteForm.resolveFailedNewStation'));
        }
      }
    } catch (e: any) {
      // 闸门二（2.2 需求点1）的拒绝要给明确指引：重试永远过不去，而站点此刻**已经**被写成
      // 「确认无法建联」了 —— 运营必须知道条目没关闭、要刷新后重新处理，否则会以为只是网络抖动。
      // 其余失败保持原有兜底文案不变。
      const keys: string[] = (e?.graphQLErrors ?? [])
        .map((ge: any) => ge?.extensions?.messageKey)
        .filter(Boolean);
      if (keys.includes('pickup.review_pool.unreachable_not_allowed')) {
        message.error(t('pickup.addressReviewPool.abnormal.resolveUnreachableRejected'));
      } else {
        // 与上面两个分支同口径：新建路径不能提示「重试」（理由见 resolveAsNewStation 分支的注释）
        message.error(
          t(
            siteDrawerMode === 'edit'
              ? 'pickup.addressReviewPool.siteForm.resolveFailed'
              : 'pickup.addressReviewPool.siteForm.resolveFailedNewStation',
          ),
        );
      }
    }
  };

  const [resolveAsConfirmAppointment] = useMutation<
    ResolveReviewPoolAsConfirmAppointmentResponse,
    { input: ResolveReviewPoolExistingStationInput }
  >(RESOLVE_REVIEW_POOL_AS_CONFIRM_APPOINTMENT);

  const [confirmPickupAppointments] = useMutation<
    ConfirmPickupAppointmentsResponse,
    { appointmentIds: number[] }
  >(CONFIRM_PICKUP_APPOINTMENTS);

  // 确认当日预约单（历史存量·未联系到·当日有单）：
  //  1) confirmPickupAppointments 把当日单 confirm 置 1（迁到「确认预约单」页签，= PRD「确认当日预约单」本义）；
  //  2) resolveAsConfirmAppointment：mark processed(5) + 回填解卡（订单拉到今天→重聚合进当日预约单）。
  const handleConfirmAppointment = async () => {
    if (item.relatedWarehouseId == null) {
      message.error(t('pickup.addressReviewPool.appointment.noRelatedStation'));
      return;
    }
    setConfirmApptLoading(true);
    try {
      // 先确认预约单本身（有当日单 id 才调；理论上 hasTodayAppointment 必带 id）
      if (item.todayAppointmentId != null) {
        const confirmResp = await confirmPickupAppointments({
          variables: { appointmentIds: [item.todayAppointmentId] },
        });
        if (!confirmResp.data?.confirmPickupAppointments) {
          message.error(t('pickup.addressReviewPool.appointment.failed'));
          return;
        }
      }
      const resp = await resolveAsConfirmAppointment({
        variables: {
          input: {
            reviewPoolId: item.id,
            targetCooperationWarehouseId: item.relatedWarehouseId,
            // 首次建联(电联1)联络记录截图：与「创建新站点」路径同一口径带上，
            // 否则 pickup 侧 op3 无 content，跟进记录的首次建联节点显示为空、凭证丢失
            proofS3KeysJson: proofKeys.length ? JSON.stringify(proofKeys) : undefined,
          },
        },
      });
      if (resp.data?.resolveReviewPoolAsConfirmAppointment) {
        message.success(t('pickup.addressReviewPool.appointment.confirmed'));
        setConfirmApptOpen(false);
        resolvedAndClearDraft();
      } else {
        message.error(t('pickup.addressReviewPool.appointment.failed'));
      }
    } catch (e) {
      message.error(t('pickup.addressReviewPool.appointment.failed'));
    } finally {
      setConfirmApptLoading(false);
    }
  };

  // 创建预约单（手工建单抽屉）成功后：标记池条目 processed(5) + 回填解卡（释放卡单，由聚合调度器并入刚建的当日单，不会双建）。
  const handleCreateAppointmentSuccess = async () => {
    setCreateApptOpen(false);
    if (item.relatedWarehouseId == null) {
      resolvedAndClearDraft();
      return;
    }
    try {
      const resp = await resolveAsConfirmAppointment({
        variables: {
          input: {
            reviewPoolId: item.id,
            targetCooperationWarehouseId: item.relatedWarehouseId,
            // 首次建联(电联1)联络记录截图：与「创建新站点」路径同一口径带上，
            // 否则 pickup 侧 op3 无 content，跟进记录的首次建联节点显示为空、凭证丢失
            proofS3KeysJson: proofKeys.length ? JSON.stringify(proofKeys) : undefined,
          },
        },
      });
      // 建单抽屉已弹"Appointment creation successful!",此处 resolve 仅后台标记条目已处理,
      // 成功不再重复弹 toast(避免双提示);仅失败时告警需人工刷新。
      if (!resp.data?.resolveReviewPoolAsConfirmAppointment) {
        message.warning(
          t('pickup.addressReviewPool.appointment.markProcessedFailed'),
        );
      }
    } catch (e) {
      message.warning(
        t('pickup.addressReviewPool.appointment.markProcessedFailed'),
      );
    } finally {
      resolvedAndClearDraft();
    }
  };

  const [resolveAsAbMapping] = useMutation<
    ResolveReviewPoolAsAbMappingResponse,
    { input: ResolveReviewPoolAbMappingInput }
  >(RESOLVE_REVIEW_POOL_AS_AB_MAPPING);

  const handleAbSubmit = async (payload: AbMappingSubmit) => {
    try {
      const resp = await resolveAsAbMapping({
        variables: {
          input: {
            reviewPoolId: item.id,
            targetBCooperationWarehouseId: payload.targetSiteId,
            mappings: payload.mappings,
            fullySelected: payload.fullySelected,
          },
        },
      });
      if (resp.data?.resolveReviewPoolAsAbMapping) {
        message.success(t('pickup.addressReviewPool.abWizard.submitted'));
        resolvedAndClearDraft();
      } else {
        message.error(t('pickup.addressReviewPool.abWizard.failed'));
      }
    } catch (e) {
      // 「目标站点不可用」是常态并发：运营打开卡片时目标还能选，提交时已被别人标成「确认无法建联」。
      // 前端虽已按 linkStatus 置灰，拿的却是旧快照。服务端把该原因经 extensions.messageKey 透了出来
      // （PickupReviewPoolMutation#orThrowKnownBizElseLogFailure + i18n 的 pickup.review_pool.*），
      // 这里必须翻出来 —— 否则运营只看到通用「操作失败」，会反复重试并报障。
      // 用 translateGraphQLError 而非裸 t(messageKey)：未登记的码（如 resolve_failed）会自动回落兜底文案。
      message.error(translateGraphQLError(e, 'pickup.addressReviewPool.abWizard.failed'));
    }
  };

  const handleMergeSubmit = async (targetSiteId: number) => {
    try {
      const resp = await mergeToExisting({
        variables: { input: { reviewPoolId: item.id, targetCooperationWarehouseId: targetSiteId } },
      });
      if (resp.data?.resolveReviewPoolAsMergeToExisting) {
        message.success(t('pickup.addressReviewPool.mergeWizard.submitted'));
        resolvedAndClearDraft();
      } else {
        message.error(t('pickup.addressReviewPool.mergeWizard.failed'));
      }
    } catch (e) {
      // 同 handleAbSubmit：把「目标站点已确认无法建联」透出来，别退化成通用失败
      message.error(translateGraphQLError(e, 'pickup.addressReviewPool.mergeWizard.failed'));
    }
  };

  // 切换地址时重置联系结果、凭证与各操作弹窗
  useEffect(() => {
    // 联系结果与两组凭证由 useReviewContactDraft 按条目恢复/清空，此处不再手工重置，
    // 否则会把刚恢复出来的草稿又清掉。
    setContactsOpen(false);
    setViewOpen(false);
    setModifyOpen(false);
    setSiteDrawerOpen(false);
    setAbOpen(false);
    setMergeOpen(false);
    setConfirmApptOpen(false);
    setAbnormalModalOpen(false);
  }, [item.id]);

  const isStandardizeFailed = item.diagnosisType === ReviewDiagnosisType.StandardizeFailed;
  const isSimilar = item.diagnosisType === ReviewDiagnosisType.Similar;
  /**
   * 2.2 需求点1：该条目当前有有效的地址异常上报。
   *
   * <p>它与「诊断=标准化失败」在流程上等价 —— 两者都免司机上门、直接进二次建联，
   * 所以下面所有 `isStandardizeFailed && 未联系到` 的门禁都要把它并进来
   * （与 getOperationPlan 的 abnormalNoOnsite 同一口径，改一处必须同步另一处）。
   */
  const abnormalReported = !!item.abnormalReport;
  /** 走两步免上门流程（标准化失败 或 已上报地址异常）且首联未联系到 */
  const noOnsiteTwoStep = (isStandardizeFailed || abnormalReported) && contact === 'not_contacted';
  /**
   * 「二次联系结果确认」这一节点当前是否<b>真的渲染出来了</b>。
   *
   * <p>编号与渲染<b>必须共用这一个判据</b> —— 这是 2.1 就存在的一个跳号缺陷：
   * 渲染条件带 {@code proofKeys.length > 0}，而编号条件只判 noOnsiteTwoStep，
   * 于是「标准化失败 + 未联系到 + 联络截图未传」时节点2 不渲染、节点3 却仍编号成 3，
   * 界面表现为 <b>1 → 3 跳号，节点 ② 凭空消失</b>（main 上原样存在，非 2.2 引入）。
   *
   * <p>本变量让「选择操作」的编号恒等于「前面实际渲染的节点数 + 1」，结构上不可能再跳号。
   * 副作用是传截图前显示 2、传完变 3 —— 这是节点动态出现的自然表现，比恒定跳号好。
   */
  const secondContactStepVisible = noOnsiteTwoStep && proofKeys.length > 0;
  /**
   * PRD 需求点1：「创建揽收站点后，其他备注自动、默认填充：异常地址—$提交原因」。
   *
   * <p>做成**表单默认值**而不是后端强制写入 —— PRD 说的是"默认填充"，运营应当看得见、改得动。
   * 写「其他备注」(other_remark) 而不是「备注信息」(remark)：后者是运营手填的必填字段，
   * 覆盖它会把现场信息冲掉且无处可查。全链路已通（buildSaveData → api-server → other_remark）。
   *
   * <p><b>字段口径已定案（2026-09-07，产品确认）：就是「其他备注」，不是「备注信息」。</b>
   * 交互原型曾把这句话画在「备注信息」里，与 PRD 文字不一致 —— 以 PRD 为准。
   * 别再改回 remark：那是运营手填的必填字段，覆盖它会把现场信息冲掉且无处可查。
   */
  const abnormalRemarkPrefill = (() => {
    const reason = item.abnormalReport?.reason?.trim();
    // 原因自 2026-09-09 起必填（前后端双校验），正常不会走到这个分支。
    // 判空仍保留：站点侧展示的是历史 operation_log，2026-09-09 之前上报的记录允许无原因；
    // 那种情况下 PRD 的「异常地址—$提交原因」没有内容可填，硬塞占位反而像系统在指责运营没填。
    if (!reason) return undefined;
    const prefix = t('pickup.addressReviewPool.abnormal.remarkPrefillPrefix');
    // other_remark 是 varchar(255)，原因本身也可到 255 ⇒ 拼上前缀会超长被库拒绝，这里先截断
    return `${prefix}${reason}`.slice(0, 255);
  })();

  const [abnormalModalOpen, setAbnormalModalOpen] = useState(false);
  /** true = 编辑既有上报（预填），false = 新增 */
  const [abnormalEditing, setAbnormalEditing] = useState(false);
  /**
   * 地址异常上报灰度（`pickup.address_abnormal_report.gray_switch`），**独立于 2.0 灰度**。
   * 只约束"新报/编辑"：已上报的卡片与撤销不受约束 —— 关开关止血后，已上报的记录仍然改变着
   * 流程（免上门直进二次建联），运营必须看得见它、并且能撤回去（后端撤销也刻意不校验灰度）。
   */
  const { abnormalReportEnabled } = useAddressAbnormalGray();
  const abnormalGrayEnabled = abnormalReportEnabled(item.warehouseId);
  // 未联系到客户时，「创建新站点（未建联）」需先上传联络记录截图，未上传则第二步操作置灰
  const proofMissing = contact === 'not_contacted' && proofKeys.length === 0;
  // 2.1：二次未联系到同样需留凭证；两组都齐才放行「确认无法建联」终态操作
  const secondProofMissing = secondContact === 'not_contacted' && secondProofKeys.length === 0;
  /**
   * 把 displayUrls 补齐到与 keys 等长，缺的位置留空串。
   *
   * <p>`ImageUploader.fileListFromProps` 只在 `value.length === displayUrls.length` 时渲染既有项，
   * 否则**静默返回空列表**（maxCount>1 时没有兜底分支）。而后端 `s3KeysToUrls` 是刻意允许
   * urls 短于 keys 的——单张预签名失败就 mapNotNull 丢掉那一张（见 ReviewPoolTypes 注释）。
   * 两者叠起来的后果：草稿里有 key、界面却是个空的必填上传框，而门禁读 `keys.length` 判为已上传
   * ⇒「没传二次截图，『更新原站点（确认无法建联）』却可点」。
   *
   * <p>补齐后那一项会以裂图形式出现，但**可见即可删**：运营删掉后 keys 变空、按钮自动置灰，
   * 状态能自愈。不改共享 ImageUploader 是刻意的——它有 34 个调用方（订单关闭 / 异常标记 / POD /
   * 车辆 / 司机等），改渲染兜底会一次性影响全部，风险远大于收益。
   */
  const padUrls = (keys: string[], urls: string[]) =>
    keys.length === urls.length ? urls : keys.map((_, i) => urls[i] ?? '');
  const plan = getOperationPlan(item, contact, !proofMissing, secondContact, !secondProofMissing);
  /**
   * 走完两步且两次都没联系到 = 免上门直达「确认无法建联」的终态分支。
   * 2.2：已上报地址异常的条目走同一条终态（判据见 noOnsiteTwoStep）。
   */
  const isUnreachableTerminal = noOnsiteTwoStep && secondContact === 'not_contacted';

  const hintText = (() => {
    const base = t(`pickup.addressReviewPool.hint.${plan.diagnosisHintKey}`);
    if (plan.diagnosisHintKey === 'similar' && item.similarCount) {
      return `${base}（${item.similarCount}）`;
    }
    return base;
  })();

  // 审核池建站：状态按联系结果预置且不可改（已联系到→已建联；未联系到→待上门）
  // 2.1 需求点1：标准化失败且两次电联均未联系到 → 免司机上门，直接预置「确认无法建联」
  const presetLinkStatus = isUnreachableTerminal
    ? LinkStatus.Unreachable
    : contact === 'contacted'
      ? LinkStatus.Linked
      : contact === 'not_contacted'
        ? LinkStatus.Pending
        : undefined;

  /**
   * 站点抽屉是否豁免地址标准化。口径与站点管理页一致：站点**是或将是**「确认无法建联(link5)」。
   * 这类站点的地址在审核池就标准化失败、两次电联又联系不到客户，但要保留下来等以后联系上再转已建联；
   * 若照常拦截，整条"保留后续再建联"的路会被门禁断掉。
   *
   * <p>hash 必须与它同真同假：后端 buildAnalyzedFromRequest 直接取入参、不兜底计算，
   * 而 save() 是全列覆盖 UPDATE —— 跳过标准化却没带 hash 会把站点命中键写成 NULL。
   * edit 用 cw 自己的 hash（editValues 透传），create 用审核池条目的 hash（站点还不存在）。
   */
  const presetCoreHash =
    siteDrawerMode === 'edit' ? editValues.addressCoreHash : item.addressCoreHash;
  const presetAnalysisHash =
    siteDrawerMode === 'edit' ? editValues.addressAnalysisHash : item.addressAnalysisHash;
  /**
   * ⚠️ 必须带 `!!presetCoreHash` 前提：doSave 里「skipStandardize 为真却没有 core_hash」会直接
   * message.error 返回（因为 buildAnalyzedFromRequest 不兜底、save() 全列覆盖会把命中键写成 NULL）。
   * cw.address_core_hash 是可空列（1.0 老站点可能没有），无条件豁免会让这类站点从"被拦"退化成
   * "点确定就报错"——更糟。没有 hash 时宁可走正常标准化路径：它本来就缺命中键，标准化反而是修数据。
   */
  const skipStandardizeForSite =
    (isUnreachableTerminal || editDbLinkStatus === LinkStatus.Unreachable) && !!presetCoreHash;

  /**
   * 两组凭证的提交形态：
   * - 终态分支 → 分组对象 `{"first":[...],"second":[...]}`，pickup 侧拆成 op3+op6 两条跟进记录
   * - 其余分支 → 保持 2.0 的数组形态，行为不变
   */
  const buildProofS3KeysJson = (): string | undefined => {
    if (isUnreachableTerminal) {
      return JSON.stringify({ first: proofKeys, second: secondProofKeys });
    }
    return proofKeys.length ? JSON.stringify(proofKeys) : undefined;
  };

  // 按 id 拉取既有 cw 并映射；站点不存在(悬挂/已删)→warning，查询异常→error；返回 null 表示不继续
  const loadCwForm = async (id: number): Promise<{ values: Partial<PickupSiteFormValues>; address: SiteAddressForSave } | null> => {
    try {
      const { data } = await loadStation({ variables: { id } });
      const v = data?.getCooperationWarehouseById;
      if (!v) {
        message.warning(t('pickup.addressReviewPool.siteForm.stationNotFound', { id }));
        return null;
      }
      return mapCwToForm(v);
    } catch {
      message.error(t('pickup.addressReviewPool.siteForm.loadFailed'));
      return null;
    }
  };

  /**
   * 查看既有站点（历史存量 chip / 相似站点）：只读打开，从 cw 带出原始信息。
   *
   * <p>是否显示「✓ 已标准化」由 `mapCwToForm` 的统一判据决定（isAddressStandardized：只有
   * cw.standardize_status===1 才算已标准化），此处不再按审核池条目的 diagnosisType 另判一套——
   * 那会让「查看相似站点」拿卡片的诊断去标注另一个站点，也是先前"卡片标失败、详情显示已标准化"
   * 这类自相矛盾的来源。只读模式下未标准化只是**不显示绿框**，不会冒出保存类提示（被 !readOnly 挡住）。
   */
  const openViewStation = async (id: number) => {
    const r = await loadCwForm(id);
    if (!r) return;
    setViewId(id);
    setViewAddress(r.address);
    setViewValues(r.values);
    setViewOpen(true);
  };

  // 历史存量·更新站点：从既有站点 cw 预填编辑表单（带出原始信息供修改，而非重新录入）。
  // G7：审核池抽屉 statusEditable=false，故按联系结果预置建联状态（已联系→已建联）覆盖原站当前值，
  // 否则原站「待上门」被锁死、无法在此建联；presetLinkStatus 为空时保留原值。
  const openUpdateStation = async (id: number) => {
    const r = await loadCwForm(id);
    if (!r) return;
    // cw 的 link_status 原值：editValues.linkStatus 会被 presetLinkStatus 覆盖，读它拿不到原值，
    // 而「原站点本来就是确认无法建联」是豁免地址标准化的判据之一
    setEditDbLinkStatus(r.values.linkStatus);
    setEditValues({
      ...r.values,
      linkStatus: presetLinkStatus ?? r.values.linkStatus,
      // PRD 需求点1：更新站点路径同样预填其他备注，但**只在站点原本为空时**填 ——
      // 历史存量站点的 other_remark 可能有运营写的现场信息，覆盖掉就找不回来了。
      otherRemark: r.values.otherRemark?.trim() ? r.values.otherRemark : abnormalRemarkPrefill,
    });
    setEditAddress(r.address);
    setSiteDrawerMode('edit');
    setSiteDrawerOpen(true);
  };

  const siteInitialValues: Partial<PickupSiteFormValues> = {
    // 命名规则见 buildSuggestedSiteName；本入口建的站就落在条目地址上，故取条目自身地址与联系人
    name: buildSuggestedSiteName({
      contactName: item.contactPersons.find((c) => !c.inBlacklist)?.name,
      addressLine1: item.addressLine1,
      zipcode: item.zipcode,
      cwId: draftCwId,
    }),
    // 关联客户预填审核池汇总的当日订单渠道（卡片/详情已展示同一份数据），运营可增删
    relatedClientIds: item.channelIds,
    dock: item.dock,
    linkStatus: presetLinkStatus,
    // PRD 需求点1：已上报地址异常时预填其他备注。新建态本就为空，直接填不会覆盖任何东西
    otherRemark: abnormalRemarkPrefill,
    // 方案A 一致拆分：inBlacklist 号码归「黑名单配置」区，其余为普通联系人
    contactPersons: item.contactPersons
      .filter((c) => !c.inBlacklist)
      .map((c) => ({
        name: c.name,
        phoneCountryCode: normalizeCc(c.phoneCountryCode),
        phone: c.phone,
      })),
    blacklistContacts: item.contactPersons
      .filter((c) => c.inBlacklist)
      .map((c) => ({
        phoneCountryCode: normalizeCc(c.phoneCountryCode),
        phone: c.phone,
      })),
  };

  // 详情展示：主联系人（优先未拉黑）+ 是否有更多 + 诊断标签
  const primaryContact = item.contactPersons.find((c) => !c.inBlacklist) || item.contactPersons[0];
  const diagTag = (() => {
    switch (item.diagnosisType) {
      case ReviewDiagnosisType.New:
        return { text: t('pickup.addressReviewPool.diagnosis.new'), color: 'green' };
      case ReviewDiagnosisType.StandardizeFailed:
        return { text: t('pickup.addressReviewPool.diagnosis.standardizeFailed'), color: 'red' };
      case ReviewDiagnosisType.Similar:
        return {
          text: `${t('pickup.addressReviewPool.diagnosis.similar')} ${item.similarCount ?? ''}`.trim(),
          color: 'orange',
        };
      default:
        return { text: '', color: 'default' };
    }
  })();

  // 新建站点：先 createDraft 预占拿真实 cw.id（供表单展示/后续建群），再开 create drawer；保存时带该 id 转正。
  // sourceKey 按审核池条目维度，取消后重进复用同一 id（与 AB 建 B 站的 :ab 区分，两者是不同草稿）。
  const openCreateSiteWithDraft = async () => {
    try {
      const resp = await createDraft({
        variables: { hubId: item.warehouseId, sourceKey: `review:${item.id}:main` },
      });
      const id = resp.data?.createDraftCooperationWarehouse;
      if (id == null) {
        message.error(t('pickup.addressReviewPool.siteForm.saveFailed'));
        return;
      }
      setDraftCwId(Number(id));
      setSiteDrawerMode('create');
      setSiteDrawerOpen(true);
    } catch {
      message.error(t('pickup.addressReviewPool.siteForm.saveFailed'));
    }
  };

  // 关闭建站 drawer：不删草稿占位。运营可能已拿该 id 建了微信群，删掉会导致重进换 id、群名错位；
  // 保留草稿则重进复用同一 id，超 24h 未转正由后端清理任务物理删。
  const handleSiteDrawerClose = () => {
    setSiteDrawerOpen(false);
    setDraftCwId(undefined);
  };

  /**
   * 点击「AB地址映射」的**入口拦截**（PRD：点击AB映射操作后 → 判断当前站点在当日以后是否存在
   * 非已创建、已取消的预约单 → 是，弹窗提示请取消或延后；否，进入操作）。
   *
   * 传 `targetB=null` + `fullySelected=true`：这一刻运营还没选 B、还没勾维度，
   * 后端按「跳过自指判定」处理，拦截是 allowed 之后的第一道，不会被其它 reason 抢先返回。
   *
   * **只认 HAS_IN_PROGRESS_APPOINTMENT 一个原因**，其余一律放行进向导 —— 它们讲的是
   * 「会不会整站作废」，属 PRD 的「操作后」分支，由提交前那次预览（confirmRetireImpact）处理。
   * 在入口就拿量级超限拦人，会让运营连映射都做不了，而映射本身是允许的。
   *
   * 查询失败按**放行**处理：拦截是保护而非鉴权，提交前还会再查一次；
   * 入口这次网络抖动不该把运营卡在门外。
   */
  const guardAbMappingEntry = async (): Promise<boolean> => {
    // 只有历史存量 + 有关联站点的条目才可能整站作废（后端 applicable 判据）。
    // 其余条目后端必然返回 NOT_APPLICABLE 后放行，这里提前短路，省掉一次无意义的往返。
    if (item.sourceTag !== ReviewSourceTag.History || item.relatedWarehouseId == null) return true;
    setAbEntryChecking(true);
    try {
      const { data } = await previewAbRetireForEntry({
        variables: { reviewPoolId: item.id, targetBCooperationWarehouseId: null, fullySelected: true },
      });
      const p = data?.previewAbRetire;
      if (p?.blockedReason !== AbRetireBlockedReason.HasInProgressAppointment) return true;
      const rows = p.blockingAppointments ?? [];
      const dates = Array.from(
        new Set(rows.map((a) => a.expectedPickupDate).filter((d): d is string => !!d)),
      ).join('、');
      const numbers = rows.map((a) => a.appointmentNumber ?? '-').join('、');
      Modal.error({
        title: aw('abEntryBlockedTitle'),
        content: aw('abEntryBlockedInProgress').replace('{dates}', dates).replace('{numbers}', numbers),
        okText: t('common.confirm', '确定') as string,
      });
      return false;
    } catch {
      return true;
    } finally {
      setAbEntryChecking(false);
    }
  };

  const handleOperation = async (op: ReviewOperation) => {
    switch (op) {
      case ReviewOperation.ModifyAddress:
        setModifyOpen(true);
        return;
      case ReviewOperation.CreateSite:
        openCreateSiteWithDraft();
        return;
      case ReviewOperation.UpdateSite:
        // 历史存量·更新站点：从既有 cw 预填后再打开编辑
        if (item.relatedWarehouseId != null) openUpdateStation(item.relatedWarehouseId);
        return;
      case ReviewOperation.AbMapping:
        if (!(await guardAbMappingEntry())) return;
        setAbOpen(true);
        return;
      case ReviewOperation.MergeToExisting:
        setMergeOpen(true);
        return;
      case ReviewOperation.ConfirmAppointment:
        setConfirmApptOpen(true);
        return;
      case ReviewOperation.CreateAppointment:
        // 手工建单：复用预约单页建单抽屉，预填历史存量站点（区别于 confirm 的自动确认）
        setCreateApptOpen(true);
        return;
      default:
        // 理论不可达（操作矩阵已覆盖全部枚举），保留兜底
        message.info(t(`pickup.addressReviewPool.operation.${op as string}`) as string);
    }
  };

  return (
    <div className="space-y-4">
      {/* 详细信息头（历史存量：站点 ID 收在这里，点击查看只读详情） */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-[15px] font-semibold text-gray-800">
          <span className="flex items-center gap-1">📋 {t('pickup.addressReviewPool.detail.title')}</span>
          {item.relatedWarehouseId != null && (
            <span
              // 传 isStandardizeFailed：与卡片徽标同一判据（都是 diagnosisType===StandardizeFailed），
              // 保证「卡片标标准化失败、点进去显示已标准化」不会再出现
              onClick={() => openViewStation(item.relatedWarehouseId as number)}
              className="cursor-pointer rounded-md bg-purple-50 px-2 py-0.5 text-sm font-medium text-purple-600"
            >
              {t('pickup.addressReviewPool.list.stationId')}: {item.relatedWarehouseId}
              {relatedStationName ? ` · ${relatedStationName}` : ''} ›
            </span>
          )}
        </div>
        <Tag color="purple" className="!m-0">
          {t('pickup.addressReviewPool.detail.pending')}
        </Tag>
      </div>

      {/* 标准化后地址 */}
      <section>
        <div className="mb-2 flex items-center gap-1 font-medium text-gray-700">
          🧭 {t('pickup.addressReviewPool.detail.standardizedAddress')}
        </div>
        <div className="rounded-lg border border-gray-200 p-3">
          <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_72px_92px] gap-3 text-xs text-gray-400">
            <span>{t('pickup.addressReviewPool.detail.colAddress')}</span>
            <span>{t('pickup.addressReviewPool.detail.colCity')}</span>
            <span>{t('pickup.addressReviewPool.detail.colState')}</span>
            <span>{t('pickup.addressReviewPool.detail.colZip')}</span>
          </div>
          <div className="mt-1 grid grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_72px_92px] gap-3 text-sm text-gray-800">
            <span>{item.addressLine1 || item.displayAddress}</span>
            <span>{item.city || '-'}</span>
            <span>{item.state || '-'}</span>
            <span>{item.zipcode || '-'}</span>
          </div>
        </div>
      </section>

      {/* 联系人信息 */}
      <section>
        <div className="mb-2 flex items-center gap-1 font-medium text-gray-700">
          👤 {t('pickup.addressReviewPool.detail.contactInfo')}
        </div>
        <div className="rounded-lg border border-gray-200 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-4 text-sm text-gray-800">
              <span className="flex items-center gap-1">
                👤 <b>{primaryContact?.name || '-'}</b>
              </span>
              <span className="flex items-center gap-1">
                📞 {primaryContact ? formatPhone(primaryContact) || '-' : '-'}
              </span>
              {primaryContact?.inBlacklist && (
                <Tag color="red" icon={<StopOutlined />} className="!m-0">
                  {t('pickup.addressReviewPool.list.blacklisted')}
                </Tag>
              )}
            </div>
            {item.contactPersons.length > 0 && (
              <span
                onClick={() => setContactsOpen(true)}
                className="shrink-0 cursor-pointer text-sm font-medium text-purple-600"
              >
                {t('pickup.addressReviewPool.detail.viewMoreContacts')} ›
              </span>
            )}
          </div>
          <div className="my-2 border-t border-gray-100" />
          <div className="flex items-center gap-2 text-sm text-gray-600">
            🛍 <span className="text-gray-400">{t('pickup.addressReviewPool.list.channel')}</span>
            <span>{item.channelNames.join('、') || '-'}</span>
          </div>
        </div>
      </section>

      {/* 地址诊断 */}
      <section>
        <div className="mb-2 flex items-center gap-1 font-medium text-gray-700">
          🩺 {t('pickup.addressReviewPool.detail.diagnosis')}
        </div>
        <div
          className={`rounded-lg border p-3 ${
            isStandardizeFailed || isSimilar ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'
          }`}
        >
          <div className="flex items-start gap-2">
            <Tag
              color={isStandardizeFailed || isSimilar ? 'red' : 'green'}
              className="!m-0 shrink-0"
            >
              {isSimilar ? t('pickup.addressReviewPool.diagnosis.similarSite') : diagTag.text}
            </Tag>
            <span className="text-sm text-gray-700">
              {isSimilar
                ? t('pickup.addressReviewPool.hint.similarCount', {
                    count: item.similarCount ?? item.similarStations?.length ?? 0,
                  })
                : hintText}
            </span>
          </div>
          {/*
            当日单量（2.1 需求点6）：附在诊断信息里，帮运营判断这个地址值不值得建站。
            历史存量条目不展示 —— 它是 golive 从 cooperation_warehouse 直插的、不关联订单，
            恒为 0，显示出来会让运营误以为这个站点没生意。
          */}
          {item.sourceTag !== ReviewSourceTag.History && (
            <div className="mt-2 text-sm text-gray-600">
              {t('pickup.addressReviewPool.todayOrders.detail', { count: item.todayOrderCount ?? 0 })}
            </div>
          )}
          {isSimilar && item.similarStations?.length ? (
            <div className="mt-3 space-y-2 border-t border-dashed border-red-200 pt-3">
              {item.similarStations.map((s) => {
                const [cName, cPhone] = (s.contact ?? '').split(/[·|]/).map((x) => x.trim());
                return (
                  <div key={s.id} className="flex items-center gap-4 rounded-lg border border-gray-200 bg-white p-3">
                    <div className="shrink-0 text-center">
                      <div className="text-xs text-gray-400">{t('pickup.addressReviewPool.list.stationId')}</div>
                      <div className="text-base font-bold text-purple-600">{s.id}</div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{s.address}</span>
                        {/* 相似诊断照常把「确认无法建联」的站点算进来（地址库里确实已有这个地址），
                            但要让运营一眼看出它不能作为合并/AB 的目标，否则只能到提交时才发现 */}
                        {s.linkStatus === LinkStatus.Unreachable && (
                          <Tag color="default" className="!m-0">
                            {t('pickup.addressReviewPool.operation.targetUnreachable')}
                          </Tag>
                        )}
                      </div>
                      {s.contact && (
                        <div className="mt-1 flex items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">👤 {cName || s.contact}</span>
                          {cPhone && <span className="flex items-center gap-1">📞 {cPhone}</span>}
                        </div>
                      )}
                    </div>
                    <span
                      onClick={() => openViewStation(s.id)}
                      className="shrink-0 cursor-pointer text-sm font-medium text-purple-600"
                    >
                      {t('pickup.addressReviewPool.detail.view')} ›
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </section>

      {/* 联系结果确认（所有诊断均先确认联系结果，对齐原型；标准化失败也走此步） */}
      <section>
          <div className="mb-2 flex items-center gap-2 font-medium text-gray-700">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-600 text-xs text-white">
              1
            </span>
            {t('pickup.addressReviewPool.detail.contactResult')}
          </div>
          {/* 草稿恢复提示：单选框与截图凭空预填会让运营以为是系统判定的，必须说明来源 */}
          {draftRestored && (
            <div className="mb-2 rounded-lg border border-blue-200 bg-blue-50 p-2 text-xs text-gray-600">
              📝 {t('pickup.addressReviewPool.contact.draftRestored')}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div
              onClick={() => setContact('contacted')}
              className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                contact === 'contacted' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
              }`}
            >
              <div className="flex items-center gap-2 font-medium text-gray-800">
                <Radio checked={contact === 'contacted'} onChange={() => setContact('contacted')} />
                {t('pickup.addressReviewPool.contact.contacted')}
              </div>
              <div className="mt-1 text-xs text-gray-400">
                {isStandardizeFailed
                  ? t('pickup.addressReviewPool.contact.contactedDescStdFail')
                  : t('pickup.addressReviewPool.contact.contactedDesc')}
              </div>
            </div>
            <div
              onClick={() => setContact('not_contacted')}
              className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                contact === 'not_contacted' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="flex items-center gap-2 font-medium text-gray-800">
                <Radio checked={contact === 'not_contacted'} onChange={() => setContact('not_contacted')} />
                {t('pickup.addressReviewPool.contact.notContacted')}
              </div>
              <div className="mt-1 text-xs text-gray-400">
                {t('pickup.addressReviewPool.contact.notContactedDesc')}
              </div>
            </div>
          </div>
          {isStandardizeFailed && (
            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-gray-600">
              ℹ️ {t('pickup.addressReviewPool.contact.stdFailNoOnsiteTip')}
            </div>
          )}
          {contact === 'contacted' && (
            <div className="mt-2 rounded-lg border border-green-200 bg-green-50 p-2 text-xs text-gray-600">
              💬 {t('pickup.addressReviewPool.contact.contactedTip')}
            </div>
          )}
          {contact === 'not_contacted' && (
            <div className="mt-2 rounded-lg border border-gray-200 p-3">
              <div className="mb-2 text-sm font-medium text-gray-700">
                {t('pickup.addressReviewPool.contact.proofLabel')} <span className="text-red-500">*</span>
              </div>
              <div className="flex items-start gap-4">
                {/* displayUrls 必须与 value 等长同序，ImageUploader 才会渲染既有截图的缩略图
                    （见其 fileListFromProps）；本次会话新传的由组件内部状态接管，无需 URL。 */}
                <ImageUploader
                  value={proofKeys}
                  displayUrls={padUrls(proofKeys, proofUrls)}
                  onChange={setProofKeys}
                  maxCount={5}
                />
                <div className="text-xs leading-relaxed text-gray-400">
                  <div>{t('pickup.addressReviewPool.contact.proofDesc1')}</div>
                  <div>{t('pickup.addressReviewPool.contact.proofDesc2')}</div>
                  <div>{t('pickup.addressReviewPool.contact.proofDesc3')}</div>
                </div>
              </div>
            </div>
          )}
        </section>

      {/* 已上报地址异常记录（2.2 需求点1）：挂在联系结果确认之后、二次建联之前 ——
          它正是让二次建联免掉「安排司机上门」的那个前提，放在这里运营才能顺着流程读下去。
          审核池阶段站点还没建，所以撤销一定可用（不存在"已置 link5 撤不回"的情形）。

          显示条件带上 contact === 'not_contacted'：上报的前提就是首联未联系到。运营把首联改成
          「已联系到」后，后端会在下一次草稿保存里连带清掉这条上报
          （见 PickupReviewPoolProcessService.clearAbnormalReportIfPremiseGone），
          而草稿保存是防抖 + fire-and-forget 的，前端拿不到结果。这里同步隐藏，避免出现
          「卡片还在、点编辑却被后端以状态不允许拒绝」的错位。 */}
      {item.abnormalReport && contact === 'not_contacted' && (
        <AbnormalReportCard
          report={item.abnormalReport}
          reviewPoolId={item.id}
          editable={abnormalGrayEnabled}
          onOpenEdit={() => {
            setAbnormalEditing(true);
            setAbnormalModalOpen(true);
          }}
          onRevoked={() => onPatchSelected?.({ abnormalReport: undefined })}
        />
      )}

      {/* 二次联系结果确认（2.1 需求点1）：仅「诊断=标准化失败 且 首次未联系到」时出现。
          首次就联系到客户 ⇒ 该去改地址，再问二次没有意义；这与既有一次/二次建联语义一致，
          link5 的业务前提本就是"两次电联都没联系到"。 */}
      {secondContactStepVisible && (
        <section>
          <div className="mb-2 flex items-center gap-2 font-medium text-gray-700">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-600 text-xs text-white">
              2
            </span>
            {t('pickup.addressReviewPool.contact.secondResult')}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div
              onClick={() => setSecondContact('contacted')}
              className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                secondContact === 'contacted'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-green-300'
              }`}
            >
              <div className="flex items-center gap-2 font-medium text-gray-800">
                <Radio checked={secondContact === 'contacted'} onChange={() => setSecondContact('contacted')} />
                {t('pickup.addressReviewPool.contact.contacted')}
              </div>
              {/* 与第 1 步同样带一行副标题：缺了会让两个节点的卡片高度不一致（视觉错位） */}
              <div className="mt-1 text-xs text-gray-400">
                {t('pickup.addressReviewPool.contact.secondContactedDesc')}
              </div>
            </div>
            <div
              onClick={() => setSecondContact('not_contacted')}
              className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                secondContact === 'not_contacted'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="flex items-center gap-2 font-medium text-gray-800">
                <Radio
                  checked={secondContact === 'not_contacted'}
                  onChange={() => setSecondContact('not_contacted')}
                />
                {t('pickup.addressReviewPool.contact.notContacted')}
              </div>
              <div className="mt-1 text-xs text-gray-400">
                {t('pickup.addressReviewPool.contact.secondNotContactedDesc')}
              </div>
            </div>
          </div>
          {secondContact === 'contacted' && (
            <div className="mt-2 rounded-lg border border-green-200 bg-green-50 p-2 text-xs text-gray-600">
              💬 {t('pickup.addressReviewPool.contact.secondContactedTip')}
            </div>
          )}
          {secondContact === 'not_contacted' && (
            <div className="mt-2 rounded-lg border border-gray-200 p-3">
              <div className="mb-2 text-sm font-medium text-gray-700">
                {t('pickup.addressReviewPool.contact.secondProofLabel')} <span className="text-red-500">*</span>
              </div>
              <div className="flex items-start gap-4">
                <ImageUploader
                  value={secondProofKeys}
                  displayUrls={padUrls(secondProofKeys, secondProofUrls)}
                  onChange={setSecondProofKeys}
                  maxCount={5}
                />
                <div className="text-xs leading-relaxed text-gray-400">
                  <div>{t('pickup.addressReviewPool.contact.proofDesc1')}</div>
                  <div>{t('pickup.addressReviewPool.contact.proofDesc2')}</div>
                  <div>{t('pickup.addressReviewPool.contact.proofDesc3')}</div>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* 选择操作 */}
      <section>
        <div className="mb-2 flex items-center gap-2 font-medium text-gray-700">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-600 text-xs text-white">
            {secondContactStepVisible ? 3 : 2}
          </span>
          {t('pickup.addressReviewPool.detail.chooseOperation')}
        </div>
        {plan.requireContactFirst ? (
          <Alert type="warning" showIcon message={t('pickup.addressReviewPool.contact.requireFirst')} />
        ) : plan.requireProofFirst ? (
          <Alert type="warning" showIcon message={t('pickup.addressReviewPool.contact.requireProof')} />
        ) : plan.requireSecondContactFirst ? (
          <Alert type="warning" showIcon message={t('pickup.addressReviewPool.contact.requireSecond')} />
        ) : (
          <div className="space-y-3">
            {plan.guidanceKey && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 text-sm text-gray-600">
                🔗 {t(`pickup.addressReviewPool.opGuide.${plan.guidanceKey}`, {
                  count: item.similarCount ?? item.similarStations?.length ?? 0,
                })}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {plan.operations.map((op) => (
                <div key={op.key} className="flex items-center gap-2">
                  {op.prefixKey && (
                    <span className="text-sm text-gray-400">
                      {t(`pickup.addressReviewPool.opPrefix.${op.prefixKey}`)}
                    </span>
                  )}
                  <Tooltip
                    title={
                      // 未联系缺截图导致的置灰，统一给出原因提示（含历史存量的确认/创建当日预约单，
                      // 不再只限 CreateSite，否则新置灰的按钮没有任何解释）
                      op.disabled && proofMissing
                        ? t('pickup.addressReviewPool.contact.notContactedDesc')
                        : undefined
                    }
                  >
                    <Button
                      type={op.recommended ? 'primary' : 'default'}
                      disabled={op.disabled || abEntryChecking}
                      loading={abEntryChecking && op.key === ReviewOperation.AbMapping}
                      onClick={() => void handleOperation(op.key)}
                      style={
                        op.key === ReviewOperation.ModifyAddress
                          ? { background: '#ea8a3e', borderColor: '#ea8a3e' }
                          : undefined
                      }
                    >
                      {t(
                        op.labelKey
                          ? `pickup.addressReviewPool.operation.${op.labelKey}`
                          : `pickup.addressReviewPool.operation.${op.key}`,
                      )}
                    </Button>
                  </Tooltip>
                  {op.subtitleKey ? (
                    <span className="text-xs text-gray-400">
                      {t(`pickup.addressReviewPool.opSubtitle.${op.subtitleKey}`)}
                    </span>
                  ) : (
                    op.recommended &&
                    !op.prefixKey && (
                      <Tag color="purple" className="!m-0">
                        {t('pickup.addressReviewPool.operation.recommended')}
                      </Tag>
                    )
                  )}
                </div>
              ))}
            </div>
            {/* 上报地址异常（2.2 需求点1）：排在终态操作**之后**的次要入口。
                顺序是刻意的 ——「创建新站点（未建联）」是这一步的主路径，异常上报是少数情况的旁路；
                主操作在前、虚线分隔、旁路在后，运营的视线不会先撞上异常分支。别再调回去。
                只在「未联系到 + 非标准化失败 + 尚未上报」时出现（判据 canReportAddressAbnormal，
                与后端 checkReviewPoolReportable 同口径）。已上报后本入口消失，改由上方记录卡的
                「编辑 / 撤销上报」接管，避免同一件事有两个入口。 */}
            {canReportAddressAbnormal(item, contact) && !abnormalReported && abnormalGrayEnabled && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-dashed border-gray-200 pt-3">
                {/* 与同区其余操作一样受「未联系到必须先传联络截图」的顺序门禁约束：
                    第 1 步没完成，第 2 步的任何操作都不可点。此前本按钮漏了这条，
                    出现「创建新站点已置灰、上报却还能点」的错位 —— 运营可以跳过第 1 步直接上报，
                    而上报会让这个地址走上不可逆的 link5。

                    用 disabled 而不是隐藏，与 2.1 同款：按钮留着运营才知道下一步能做什么，
                    Tooltip 复用同一句原因文案，避免出现一个没有任何解释的灰按钮。 */}
                <Tooltip
                  title={proofMissing ? t('pickup.addressReviewPool.contact.notContactedDesc') : undefined}
                >
                  <Button
                    disabled={proofMissing}
                    onClick={() => {
                      setAbnormalEditing(false);
                      setAbnormalModalOpen(true);
                    }}
                  >
                    ⚠️ {t('pickup.addressReviewPool.abnormal.entryButton')}
                  </Button>
                </Tooltip>
                <span className="text-xs text-gray-400">
                  {t(
                    item.sourceTag === ReviewSourceTag.History
                      ? 'pickup.addressReviewPool.abnormal.entryHintHistory'
                      : 'pickup.addressReviewPool.abnormal.entryHintReview',
                  )}
                </span>
              </div>
            )}
          </div>
        )}
      </section>

      {/*
        todo wuhao resolve 操作接后端（缺口，本切片仅完成列表去 mock）：
        - 新建/更新站点：先调既有 saveCooperationWarehouse 拿 cw id（PickupSiteFormDrawer 目前 MOCK_CLIENTS/MOCK_VEHICLES，
          需先接真实渠道/车型查询），再调 resolveReviewPoolAsNewStation / resolveReviewPoolAsUpdateExisting；
        - 合并到已有：目标取 item.similarStations（已后端真返回）→ resolveReviewPoolAsMergeToExisting；任意站点搜索另需站点查询；
        - AB 映射：目标 B 站点需站点查询 → resolveReviewPoolAsAbMapping；
        - 预约单确认/创建：先调 api-server 既有预约单 mutation，再 resolveReviewPoolAsConfirmAppointment。
        接后端时在各 onSubmit/success 里调 onResolved?.() 刷新列表。
      */}
      {/* 查看更多联系人（弹出卡片，表格：联系人 / 联系方式） */}
      <Modal
        open={contactsOpen}
        onCancel={() => setContactsOpen(false)}
        footer={null}
        width={520}
        title={
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-purple-100">👤</span>
            <span className="font-semibold">{t('pickup.addressReviewPool.detail.contactInfo')}</span>
          </div>
        }
      >
        <div className="grid grid-cols-2 gap-x-4 border-b border-gray-100 pb-2 text-xs text-gray-400">
          <span>{t('pickup.addressReviewPool.detail.contactPerson')}</span>
          <span>{t('pickup.addressReviewPool.siteForm.phone')}</span>
        </div>
        {item.contactPersons.map((c, idx) => (
          <div key={idx} className="grid grid-cols-2 items-center gap-x-4 border-b border-gray-50 py-2 text-sm text-gray-800">
            <span className="flex items-center gap-2">
              👤 <b>{c.name || '-'}</b>
              {c.inBlacklist && (
                <Tag color="red" icon={<StopOutlined />} className="!m-0">
                  {t('pickup.addressReviewPool.list.blacklisted')}
                </Tag>
              )}
            </span>
            <span className="flex items-center gap-2">📞 {formatPhone(c) || '-'}</span>
          </div>
        ))}
      </Modal>

      {/* 查看相似站点详情（只读） */}
      <PickupSiteFormDrawer
        open={viewOpen}
        mode="view"
        statusEditable={false}
        initialValues={viewValues}
        cooperationWarehouseId={viewId}
        // 相似站点与条目同 hub（相似搜索本就按 hub 过滤）；只读态不传 warehouseId（不落库），
        // 灰度判定走 stationHubId，否则跟进记录在这个入口也出不来。
        stationHubId={item.warehouseId}
        addressForSave={viewAddress}
        onClose={() => setViewOpen(false)}
      />

      <ModifyAddressModal
        open={modifyOpen}
        item={item}
        onClose={() => setModifyOpen(false)}
        onNext={(actualAddress) => {
          // 补录完成 → 直接进 AB 向导，把 B 地址带过去。**条目地址不动**：
          // AB 的命中键取自条目 addressCoreHash，改掉就等于把 A 的 hash 换成 B 的，映射写下去命中不了。
          setAbPrefill(actualAddress);
          setAbOpen(true);
        }}
      />

      <PickupSiteFormDrawer
        open={siteDrawerOpen}
        mode={siteDrawerMode}
        statusEditable={false}
        // 2.1 需求点1：标准化失败 + 两次电联均未联系到 ⇒ 不再要求（也不可能）通过标准化，
        // 直接用条目原始地址 + 条目自带 hash 建站/更新站，与卡单同源保证后续订单命中。
        //
        // 判据刻意用「免上门终态」而不是更宽的「诊断=标准化失败」：
        // 1. 放行面不该大于需要。stdFail 下能打开本抽屉的只有终态那两个操作——「联系到客户」分支
        //    getOperationPlan 只给「修改地址」（有人可问，地址就还有救），压根不开建站抽屉。
        //    按诊断放行一个真实分支都不多解锁，只是让以后给 contacted 分支加建站时静默跳过标准化。
        // 2. hash 预置必须与之同条件。buildAnalyzedFromRequest 里 addressHash = input.addressCoreHash
        //    **没有兜底计算**，而 save() 是全列覆盖 UPDATE：一旦"跳过标准化"为真却没带 hash，
        //    原站点的 address_core_hash 会被写成 NULL、命中彻底失效。两者必须同真同假。
        // 豁免地址标准化的两种情形，统一口径 = 「站点是/将是 确认无法建联(link5)」：
        //  · isUnreachableTerminal —— 运营正要把它置为 link5（本次两次电联均未联系到）
        //  · editDbLinkStatus===5 —— 原站点本来就是 link5（保留待以后联系上再转已建联）
        // 两者的地址都标准化不了，拦下去只会让运营连名字/联系人都改不了。
        skipStandardize={skipStandardizeForSite}
        presetAddressCoreHash={skipStandardizeForSite ? presetCoreHash : undefined}
        // 标准化失败场景下地址库兜底分支让两个 hash 同值；analysis 缺失时退回 core，避免落空
        presetAddressAnalysisHash={
          skipStandardizeForSite ? presetAnalysisHash || presetCoreHash : undefined
        }
        // 更新(edit·历史存量)：从既有 cw 预填带出原始信息供改；新建(create·系统进单/BD)：用池地址/联系人
        initialValues={siteDrawerMode === 'edit' ? editValues : siteInitialValues}
        warehouseId={item.warehouseId}
        cooperationWarehouseId={siteDrawerMode === 'edit' ? item.relatedWarehouseId : draftCwId}
        // 2.2 需求点1 闸门一：置「确认无法建联」时后端要拿它校验准入（标准化失败 或 已上报地址异常），
        // 不传就会被拒绝建站。恒传，不按状态判断——判断留给抽屉内部（只在目标态是 5 时才发上去）。
        reviewPoolId={item.id}
        addressForSave={
          siteDrawerMode === 'edit'
            ? // 是否已标准化由 mapCwToForm 的统一判据给出（只有 cw.standardize_status===1 才算），
              // 不再在此硬编码 false。原先硬编码是为了绕开"mapCwToForm 不带标记 ⇒ 缺省视为已标准化"，
              // 判据下沉后这个绕法既多余、也会掩盖 cw 的真实状态。
              editAddress
            : {
                addressLine1: item.addressLine1,
                addressLine2: item.addressLine2,
                city: item.city,
                state: item.state,
                zipcode: item.zipcode,
                country: 'US',
                // 审核池创建入口一律以「未标准化」预置：入库时存的 diagnosis_type 不可信
                // （实测有 diagnosis_type=1/analysis_hash 非空、但抽屉重标却失败的条目，如 line1 带尾逗号），
                // 故首屏不显示绿色「已标准化」，强制点「地址标准化」用真实引擎校验通过后方可保存
                // （PRD 评审结论：引导用户点击地址标准化，不通过不能创建）。
                standardized: false,
              }
        }
        onClose={handleSiteDrawerClose}
        onSaved={handleSiteSaved}
      />

      <AbMappingWizard
        open={abOpen}
        // 2.2 需求点2：向导入参由 ReviewPoolItem 收窄为 AbWizardSource（它实际只用 5 个字段），
        // 好让站点编辑表单也能用同一个向导。这里逐字段搬运，审核池这条路行为不变。
        source={{
          kind: 'reviewPool',
          id: item.id,
          hubId: item.warehouseId,
          selfStationId: item.relatedWarehouseId,
          coreHash: item.addressCoreHash,
          displayAddress: item.displayAddress,
        }}
        prefill={abPrefill}
        onClose={() => {
          setAbOpen(false);
          setAbPrefill(undefined);
        }}
        onSubmit={handleAbSubmit}
      />

      <MergeToExistingWizard
        open={mergeOpen}
        item={item}
        onClose={() => setMergeOpen(false)}
        onSubmit={handleMergeSubmit}
      />

      {/* 确认预约单：复用预约单页建单抽屉 detail 只读态，按当日单 id 加载真实单；底部「确认当日预约单」→ resolve(processed_action=5) */}
      <PickupAppointmentDrawer
        visible={confirmApptOpen}
        mode="detail"
        appointmentId={item.todayAppointmentId}
        reviewConfirmMode
        reviewConfirmLoading={confirmApptLoading}
        onReviewConfirm={handleConfirmAppointment}
        onClose={() => setConfirmApptOpen(false)}
      />

      {/* 创建预约单：复用预约单页建单抽屉，预填历史存量站点（hub/timezone 走 WarehouseContext）；成功后标记 processed */}
      <PickupAppointmentDrawer
        visible={createApptOpen}
        mode="create"
        initialCooperationWarehouseId={item.relatedWarehouseId}
        onClose={() => setCreateApptOpen(false)}
        onSuccess={handleCreateAppointmentSuccess}
      />

      {/* 上报 / 编辑地址异常（2.2 需求点1）。成功后只就地更新这一个条目、**不重新拉列表** ——
          条目仍待处理，重拉会把列表重置回第一页、把运营正在处理的卡片弄丢（见 onPatchSelected）。 */}
      <ReportAddressAbnormalModal
        open={abnormalModalOpen}
        targetAddress={item.displayAddress}
        reviewPoolId={item.id}
        existing={abnormalEditing ? item.abnormalReport : undefined}
        onClose={() => setAbnormalModalOpen(false)}
        onSaved={(saved) =>
          onPatchSelected?.({ abnormalReport: buildLocalAbnormalReport(saved) })
        }
      />
    </div>
  );
};

export default ReviewAddressDetail;
